'use client'

import React, {ReactNode, forwardRef, useContext} from 'react'
import {cx} from '@local/utils/cx'
import {FormContext} from '@local/stores/contexts'

export interface ToggleProps extends React.InputHTMLAttributes<HTMLInputElement> {
	children: ReactNode,
	className?: string,
	required?: boolean,
	disabled?: boolean,
	theme?: 'light' | 'dark',
	error?: boolean,
}

export const Toggle = forwardRef<HTMLInputElement, React.PropsWithChildren<ToggleProps>>(({
	name,
	children,
	className,
	required = false,
	disabled = false,
	theme = 'light',
	error = false,
	...props
}: ToggleProps, ref) => {
	const {hasValidation} = useContext(FormContext)

	return (
		<div className={cx('relative ml-2', className)}>
			<input
				ref={ref}
				type='checkbox'
				id={name}
				name={name}
				required={required}
				className='absolute w-0 h-0 inset-0 pointer-events-none opacity-0 peer'
				aria-label={!children ? name : undefined}
				{...props} />
			<label
				htmlFor={name}
				className={cx(
					'relative inline-flex items-start gap-8 select-none',
					'font-light text-10 cursor-pointer text-grey text-left',
					hasValidation && 'peer-invalid:before:border-pink peer-invalid:after:border-pink',
					error ? 'text-pink' : theme === 'dark' ? 'text-white' : 'text-black',
					disabled && 'cursor-not-allowed opacity-60 pointer-events-none',

					// container box
					'before:block before:shrink-0 before:w-[36px] before:h-[16px]',
					'before:rounded-full before:border before:border-solid before:border-black',
					theme === 'light' && 'before:border-black',
					theme === 'dark' && 'before:border-white',
					'before:transition-colors before:duration-300 before:ease-quart-out',
					'peer-focus:before:outline before:outline-1 before:outline-offset-1',

					// pill
					'after:block after:absolute after:top-[2px] after:left-[2px] after:w-[12px] after:h-[12px] after:origin-center ',
					theme === 'light' && 'after:bg-black',
					theme === 'dark' && 'after:bg-white',
					'after:rounded-full after:border border-solid',
					'after:transition-[transform,border-color] after:duration-300 after:ease-quart-out after:transform-gpu',
					// active states
					'peer-checked:after:translate-x-[20px] peer-checked:after:border-white peer-checked:after:bg-white',
					theme === 'light' && 'peer-checked:before:!bg-black peer-checked:after:border-white peer-checked:after:bg-white',
					theme === 'dark' && 'peer-checked:before:!bg-white peer-checked:after:border-black peer-checked:after:bg-black',
				)}>
				<span>{children}</span>
			</label>
		</div>
	)
})

Toggle.displayName = 'Toggle'
